import { useContext, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import { useWishlistContext } from '../contexts/WishlistContext';
import { CartContext } from '../contexts/CartContext';
import { formatPrice, getPrimaryImage, isInStock, getStockDisplay, handleImageError } from '../utils/apiHelpers';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';

/**
 * Wishlist Page
 *
 * Displays all products in the user's wishlist
 */
function Wishlist() {
  const { items, loading, isEmpty, removeFromWishlist } = useWishlistContext();
  const { addToCart, loading: cartLoading } = useContext(CartContext);

  /**
   * Handle remove from wishlist
   */
  const handleRemove = async (productId, productName) => {
    const result = await removeFromWishlist(productId);
    if (result.success) {
      toast.success(`${productName} removed from wishlist`);
    } else {
      toast.error(result.error || 'Failed to remove from wishlist');
    }
  };

  /**
   * Handle add to cart
   */
  const handleAddToCart = async (productId, productName) => {
    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };

  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Wishlist] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={-100}
          className="paralax-section-slide-data1"
        />
      )}

      {/* Wishlist Page */}
      <div className="wishlist-page">
        <div className="container">
          <div className="row">
            <div className="col-12">
              {/* Page Header */}
              <div className="wishlist-page-heading mb-5 text-center">
                <h1 className="heading">
                  <i className="far fa-heart me-2"></i>
                  My Wishlist
                </h1>
                {!loading && !isEmpty && (
                  <p className="para_text">
                    {items.length} {items.length === 1 ? 'item' : 'items'} saved
                  </p>
                )}
              </div>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading your wishlist...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && isEmpty && (
                <div className="wishlist-empty text-center py-5">
                  <div className="empty-icon mb-4">
                    <i className="far fa-heart" style={{ fontSize: '80px', color: '#ddd' }}></i>
                  </div>
                  <h3 className="mb-3">Your Wishlist is Empty</h3>
                  <p className="text-muted mb-4">
                    Start adding collections you love to your wishlist!
                  </p>
                  <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                    Browse Collections
                  </Link>
                </div>
              )}

              {/* Wishlist Items Grid */}
              {!loading && !isEmpty && (
                <div className="wishlist-items">
                  <div className="row">
                    {items.map((item) => {
                      const product = item.product;
                      const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                      const inStock = isInStock(product);

                      return (
                        <div
                          key={item.id}
                          className="col-12 col-md-6 col-lg-4 mb-4 wow fadeInUp"
                          data-wow-delay=".2s"
                        >
                          <div className="wishlist-item-card">
                            {/* Product Image */}
                            <div className="wishlist-item-image">
                              <Link to={`/product-detail/${product.id}`}>
                                <img
                                  src={getPrimaryImage(product)}
                                  alt={product.name}
                                  onError={handleImageError}
                                />
                              </Link>

                              {/* Stock Badge */}
                              {stockInfo.className !== 'in-stock' && (
                                <span className={`badge position-absolute stock-badge ${stockInfo.className}`}>
                                  {stockInfo.className === 'out-of-stock' ? 'Out of Stock' : `Only ${product.stock} left!`}
                                </span>
                              )}

                              {/* Remove Button */}
                              <button
                                className="wishlist-item-remove-btn"
                                onClick={() => handleRemove(product.id, product.name)}
                                aria-label="Remove from wishlist"
                                title="Remove from wishlist"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>

                            {/* Product Details */}
                            <div className="wishlist-item-details">
                              <h5 className="wishlist-item-name">
                                <Link to={`/product-detail/${product.id}`}>
                                  {product.name}
                                </Link>
                              </h5>

                              {/* Price */}
                              <div className="wishlist-item-price mb-3">
                                <span className="current-price">
                                  {formatPrice(product.price)}
                                </span>
                                {product.compareAtPrice && (
                                  <span className="compare-price text-muted text-decoration-line-through ms-2">
                                    {formatPrice(product.compareAtPrice)}
                                  </span>
                                )}
                              </div>

                              {/* Category */}
                              {product.category && (
                                <p className="wishlist-item-category text-muted small mb-3">
                                  <i className="fas fa-tag me-1"></i>
                                  {product.category.name}
                                </p>
                              )}

                              {/* Added Date */}
                              <p className="wishlist-item-date text-muted small mb-3">
                                <i className="far fa-clock me-1"></i>
                                Added {new Date(item.addedAt).toLocaleDateString()}
                              </p>

                              {/* Action Buttons */}
                              <div className="wishlist-item-actions">
                                <button
                                  className="btn btn-primary btn-sm me-2"
                                  onClick={() => handleAddToCart(product.id, product.name)}
                                  disabled={!inStock || cartLoading}
                                >
                                  <i className="fas fa-shopping-cart me-1"></i>
                                  {inStock ? 'Add to Cart' : 'Out of Stock'}
                                </button>
                                <Link
                                  to={`/product-detail/${product.id}`}
                                  className="btn btn-outline-secondary btn-sm"
                                >
                                  View Details
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Continue Shopping */}
                  <div className="text-center mt-5 pt-4 border-top">
                    <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                      Continue Shopping
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Wishlist;
